import { useEffect, useId, useState } from "react";
import { Button } from "~/components/Button";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import type { EmergencyInstruction } from "~/entities/profile/emergency-instruction";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { elementsMatch } from "~/util/array";

type EmergencyInstructionFormProps = {
    instruction?: EmergencyInstruction;
    onRequestClose?: () => void;
} & FullPageModalProps;

export const EmergencyInstructionForm = ({
    instruction,
    onRequestClose,
    ...modalProps
}: EmergencyInstructionFormProps) => {
    const { handleDismiss } = useDismiss();
    const { can_update, storeInstruction } = useProfile();
    const [description, setDescription] = useState<string>(instruction?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(instruction?.photograph_ids ?? []);
    const { addToast, saving, setSaving } = useFeedback();
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const canSave = can_update && description.length > 0 && !saving;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                description !== (instruction?.description ?? "") ||
                !elementsMatch(photographs, instruction?.photograph_ids ?? []),
        );
    }, [instruction, description, photographs]);

    const doDismiss = onRequestClose ?? handleDismiss;

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const payload = {
            ...instruction,
            description,
            photograph_ids: photographs,
        };

        storeInstruction(payload)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Instruction updated",
                    duration: 3000,
                });

                doDismiss();
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    const action = instruction ? "Update" : "Add";

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${action} emergency instruction`}
            cancelable
            headerRight={() => (
                <Button title="Save" size="sm" disabled={!canSave} onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={doDismiss}
        >
            <FieldGroup>
                <TextArea
                    label="Emergency instructions"
                    value={description}
                    onChangeText={setDescription}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
